import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Mic, Send, Minimize2, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';

// --- Avatar Components ---
const MascotAvatar = ({ hasStethoscope, className, badgeSize = "text-xs" }) => (
  <div className={`relative inline-block ${className}`}>
    <img
      src="/synapse-mascot.jpg.png"
      alt="Synapse Mascot"
      className={`w-full h-full rounded-full object-cover ${
        hasStethoscope
          ? "border-2 border-gold shadow-[0_0_15px_rgba(var(--gold),0.5)]"
          : "border border-border"
      }`}
    />
    {hasStethoscope && (
      <span className={`absolute bottom-0 right-0 ${badgeSize} bg-background rounded-full leading-none shadow-sm`}>
        🩺
      </span>
    )}
  </div>
);

const HeartbeatAnimation = () => (
  <div className="flex items-center justify-center w-full h-8 overflow-hidden opacity-70">
    <svg viewBox="0 0 100 20" className="w-24 h-6 stroke-gold fill-none stroke-[2px] heartbeat-line">
      <path d="M0 10 L30 10 L35 0 L45 20 L50 10 L100 10" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    <style>{`
      .stroke-gold { stroke: hsl(var(--gold)); }
      .heartbeat-line path {
        stroke-dasharray: 200;
        stroke-dashoffset: 200;
        animation: heartbeat 1.5s linear infinite;
      }
      @keyframes heartbeat {
        0% { stroke-dashoffset: 200; }
        50% { stroke-dashoffset: 0; }
        100% { stroke-dashoffset: -200; }
      }
    `}</style>
  </div>
);

const QUICK_ACTIONS = [
  "Find a Dental Patient",
  "Library Hours",
  "Explain a pathology"
];

const INACTIVITY_GREETINGS = [
  "Need notes? Coo!",
  "Lost on campus? I can help!",
  "Quiz time? Type /quiz",
  "Any medical questions?",
  "I'm here to help, Doc!"
];

const SynapseWidget = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth - 80, y: window.innerHeight - 80 });
  const [isDragging, setIsDragging] = useState(false);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [hasStethoscope, setHasStethoscope] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [speechLang, setSpeechLang] = useState('en-US');

  const widgetRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const messagesEndRef = useRef(null);
  const inactivityTimer = useRef(null);
  const speechRecognition = useRef(null);
  const positionRef = useRef(position); // Avoid stale closure in onPointerMove
  positionRef.current = position;

  // Load state from sessionStorage
  useEffect(() => {
    const savedPos = sessionStorage.getItem('synapse_pos');
    if (savedPos) {
      try {
        const parsed = JSON.parse(savedPos);
        const x = Math.min(Math.max(0, parsed.x), window.innerWidth - 60);
        const y = Math.min(Math.max(0, parsed.y), window.innerHeight - 60);
        setPosition({ x, y });
      } catch (e) {
        console.error("Failed to parse synapse position", e);
      }
    }

    const savedStethoscope = sessionStorage.getItem('synapse_dr_coo');
    if (savedStethoscope === 'true') {
      setHasStethoscope(true);
    }

    const savedOpen = sessionStorage.getItem('synapse_open');
    if (savedOpen === 'true') {
      setIsOpen(true);
    }

    const savedHistory = sessionStorage.getItem('synapse_chat_history');
    if (savedHistory) {
      try {
        setMessages(JSON.parse(savedHistory));
      } catch(e) {}
    } else {
      setMessages([{ role: 'assistant', content: "Hi! I'm Synapse, your UMF mascot. How can I help you today? Coo!" }]);
    }

    // Init Speech Recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
      speechRecognition.current = new SpeechRec();
      speechRecognition.current.continuous = false;
      speechRecognition.current.interimResults = false;

      speechRecognition.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(prev => prev + (prev ? ' ' : '') + transcript);
        setIsRecording(false);
      };

      speechRecognition.current.onerror = (event) => {
        console.error("Speech recognition error", event.error);
        setIsRecording(false);
        toast.error("Speech recognition failed. Please try again.");
      };

      speechRecognition.current.onend = () => {
        setIsRecording(false);
      };
    }
  }, []);

  // Save chat history
  useEffect(() => {
    sessionStorage.setItem('synapse_chat_history', JSON.stringify(messages));
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Inactivity Tooltip Timer
  const resetInactivityTimer = () => {
    setTooltip(null);
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      if (!isOpen) {
        const randomGreeting = INACTIVITY_GREETINGS[Math.floor(Math.random() * INACTIVITY_GREETINGS.length)];
        setTooltip(randomGreeting);
      }
    }, 15000); // 15 seconds
  };

  useEffect(() => {
    resetInactivityTimer();
    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, messages]);

  const [wasDragged, setWasDragged] = useState(false);

  // Drag handlers
  const onPointerDown = (e) => {
    if (isOpen) return; // Disable drag when open
    // Allow clicking on tooltip to open
    if (e.target.closest('.synapse-tooltip')) return;

    // Do not e.preventDefault() on touchstart, otherwise the browser won't fire a click event later
    if (!e.touches) {
        e.preventDefault();
    }

    e.stopPropagation();
    setIsDragging(true);
    setWasDragged(false); // Reset drag state on new interaction
    setTooltip(null);

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    if (widgetRef.current) {
      const rect = widgetRef.current.getBoundingClientRect();
      dragOffset.current = {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    }
  };

  const onPointerMove = (e) => {
    if (!isDragging || isOpen) return;

    // Prevent default scrolling on mobile while dragging
    if (e.cancelable) {
      e.preventDefault();
    }

    // Mark as dragged only if movement happens
    setWasDragged(true);

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // Use positionRef to avoid stale closure - always gets current position
    const currentPos = positionRef.current;
    const newX = Math.min(Math.max(0, clientX - dragOffset.current.x), window.innerWidth - 60);
    const newY = Math.min(Math.max(0, clientY - dragOffset.current.y), window.innerHeight - 60);

    setPosition({ x: newX, y: newY });
  };

  const onPointerUp = () => {
    if (!isDragging) return;

    // We delay resetting isDragging slightly so that if the browser fires a click
    // event immediately after touchend/mouseup, toggleOpen can still read wasDragged
    setTimeout(() => {
        setIsDragging(false);
    }, 50);

    if (wasDragged) {
        sessionStorage.setItem('synapse_pos', JSON.stringify(position));
    }
    resetInactivityTimer();
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', onPointerMove);
      window.addEventListener('mouseup', onPointerUp);
      window.addEventListener('touchmove', onPointerMove, { passive: false });
      window.addEventListener('touchend', onPointerUp);
    } else {
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('mouseup', onPointerUp);
      window.removeEventListener('touchmove', onPointerMove);
      window.removeEventListener('touchend', onPointerUp);
    }
    return () => {
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('mouseup', onPointerUp);
      window.removeEventListener('touchmove', onPointerMove);
      window.removeEventListener('touchend', onPointerUp);
    };
  }, [isDragging, positionRef]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOpen = (e) => {
    // Prevent opening if the user just dragged the widget
    if (wasDragged) {
        setWasDragged(false); // Reset
        return;
    }
    const newState = !isOpen;
    setIsOpen(newState);
    sessionStorage.setItem('synapse_open', newState.toString());
    setTooltip(null);
    if (newState) {
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleSendMessage = async (text) => {
    const messageText = text || input;
    if (!messageText.trim() || isLoading) return;

    setInput('');
    resetInactivityTimer();

    // Easter Egg Check
    if (messageText.trim() === '/dr-coo') {
      setHasStethoscope(true);
      sessionStorage.setItem('synapse_dr_coo', 'true');
      setMessages(prev => [...prev, { role: 'user', content: messageText }, { role: 'assistant', content: '*Puts on tiny gold stethoscope* Coo! I feel much more professional now.' }]);
      return;
    }

    // Quiz Mode Check (Basic frontend handling to inform the user)
    const isQuizMode = messageText.trim().startsWith('/quiz');

    const newUserMsg = { role: 'user', content: messageText };
    const newMessages = [...messages, newUserMsg];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      // Get Supabase session token for auth
      const { data: sessionData } = await supabase.auth.getSession();
      const authToken = sessionData?.session?.access_token;

      const headers = { 'Content-Type': 'application/json' };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(`/api/synapse/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messages: newMessages.slice(-10), // Send last 10 messages for context
          path: location.pathname,
          is_quiz_mode: isQuizMode
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (error) {
      console.error("Synapse chat error:", error);
      setMessages(prev => [...prev, { role: 'assistant', content: "Oops! My pigeon brain had a hiccup. Please try again later. Coo!" }]);
      toast.error("Failed to connect to Synapse.");
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleRecording = () => {
    if (!speechRecognition.current) {
      toast.error("Speech recognition is not supported in this browser.");
      return;
    }

    if (isRecording) {
      speechRecognition.current.stop();
      setIsRecording(false);
    } else {
      speechRecognition.current.lang = speechLang;
      try {
        speechRecognition.current.start();
        setIsRecording(true);
      } catch(e) {
         console.error(e);
      }
    }
  };

  return (
    <>
      <div
        ref={widgetRef}
        className={`fixed z-50 flex flex-col items-end transition-transform duration-200 ease-in-out ${isDragging ? 'cursor-grabbing' : 'cursor-pointer'}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          touchAction: 'none',
        }}
        onMouseDown={onPointerDown}
        onTouchStart={onPointerDown}
      >
        {/* Tooltip */}
        {!isOpen && tooltip && !isDragging && (
          <div className="synapse-tooltip absolute right-0 bottom-full mb-4 bg-popover text-popover-foreground px-4 py-2 rounded-2xl rounded-br-sm shadow-lg text-sm font-medium border border-border animate-in fade-in slide-in-from-bottom-2 whitespace-nowrap">
            {tooltip}
          </div>
        )}

        {/* Chat Window */}
        {isOpen && (
          <div
            className="absolute w-[350px] max-w-[90vw] h-[550px] max-h-[80vh] bg-card border border-border shadow-2xl rounded-xl overflow-hidden flex flex-col"
            style={{
              bottom: position.y > window.innerHeight / 2 ? '4rem' : 'auto',
              top: position.y <= window.innerHeight / 2 ? '4rem' : 'auto',
              right: position.x > window.innerWidth / 2 ? '0' : 'auto',
              left: position.x <= window.innerWidth / 2 ? '0' : 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
             {/* Header */}
             <div className="bg-secondary p-3 flex justify-between items-center border-b border-border shadow-sm">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-background flex items-center justify-center overflow-visible">
                    <MascotAvatar hasStethoscope={hasStethoscope} className="w-8 h-8" />
                  </div>
                  <div>
                    <div className="font-bold text-foreground font-heading leading-tight">Synapse</div>
                    <div className="text-[10px] text-muted-foreground leading-tight">UMF Mascot</div>
                  </div>
                </div>
                <button onClick={toggleOpen} className="p-1.5 hover:bg-background rounded-md text-muted-foreground hover:text-foreground transition-colors">
                  <Minimize2 size={18} />
                </button>
             </div>

             {/* Message Area */}
             <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-background/50">
                {messages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'assistant' && (
                       <div className="mr-2 flex-shrink-0 mt-1">
                          <MascotAvatar hasStethoscope={hasStethoscope} className="w-6 h-6" badgeSize="text-[8px]" />
                       </div>
                    )}
                    <div
                      className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground rounded-br-sm'
                          : 'bg-card border border-border text-foreground rounded-bl-sm prose prose-sm dark:prose-invert prose-p:my-1 prose-ul:my-1 prose-li:my-0'
                      }`}
                    >
                      {msg.role === 'user' ? (
                        msg.content
                      ) : (
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      )}
                    </div>
                  </div>
                ))}

                {isLoading && (
                  <div className="flex justify-start">
                    <div className="mr-2 flex-shrink-0 mt-1">
                        <MascotAvatar hasStethoscope={hasStethoscope} className="w-6 h-6" badgeSize="text-[8px]" />
                    </div>
                    <div className="bg-card border border-border px-4 py-2 rounded-2xl rounded-bl-sm">
                       <HeartbeatAnimation />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
             </div>

             {/* Quick Actions (only if no messages or right before input) */}
             {messages.length < 3 && (
              <div className="px-3 pb-2 flex gap-2 overflow-x-auto no-scrollbar bg-background/50">
                {QUICK_ACTIONS.map((action, i) => (
                  <button
                    key={action.name || action.label || i}
                    onClick={() => handleSendMessage(action)}
                     className="whitespace-nowrap px-3 py-1.5 bg-secondary text-secondary-foreground text-xs rounded-full border border-border hover:border-primary transition-colors"
                   >
                     {action}
                   </button>
                 ))}
               </div>
             )}

             {/* Input Area */}
             <div className="p-3 border-t border-border bg-card">
                <div className="flex items-end gap-2 bg-background border border-border rounded-xl p-1 focus-within:ring-1 focus-within:ring-primary focus-within:border-primary transition-all">
                   <div className="flex flex-col items-center justify-end pb-1.5 pl-1">
                      <select
                         value={speechLang}
                         onChange={(e) => setSpeechLang(e.target.value)}
                         className="text-[10px] bg-transparent text-muted-foreground outline-none cursor-pointer appearance-none text-center font-medium mb-1"
                         title="Speech Language"
                      >
                         <option value="en-US">EN</option>
                         <option value="ro-RO">RO</option>
                         <option value="fr-FR">FR</option>
                      </select>
                      <button
                         onClick={toggleRecording}
                         className={`p-1.5 rounded-full transition-colors ${isRecording ? 'bg-destructive/20 text-destructive' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}`}
                         title="Dictate with microphone"
                      >
                        {isRecording ? <Loader2 size={16} className="animate-spin" /> : <Mic size={16} />}
                      </button>
                   </div>

                   <textarea
                     value={input}
                     onChange={(e) => setInput(e.target.value)}
                     onKeyDown={handleKeyPress}
                     placeholder="Ask Synapse a question..."
                     className="flex-1 max-h-[100px] min-h-[40px] bg-transparent resize-none outline-none text-sm p-2 text-foreground placeholder:text-muted-foreground"
                     rows={1}
                     style={{ height: input ? 'auto' : '40px' }}
                   />

                   <button
                     onClick={() => handleSendMessage()}
                     disabled={!input.trim() || isLoading}
                     className="p-2 m-0.5 rounded-lg bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
                   >
                     <Send size={16} className="ml-0.5" />
                   </button>
                </div>
             </div>
          </div>
        )}

        {/* Floating Icon */}
        <div
          className={`w-[65px] h-[65px] rounded-full shadow-[0_0_15px_rgba(var(--gold),0.5)] flex items-center justify-center transition-all ${isDragging ? 'scale-110 shadow-[0_0_20px_rgba(var(--gold),0.7)]' : 'hover:scale-105 hover:shadow-[0_0_20px_rgba(var(--gold),0.7)]'}`}
          onClick={toggleOpen}
        >
          <MascotAvatar hasStethoscope={hasStethoscope} className="w-full h-full" badgeSize="text-lg" />
          {/* Unread dot (optional) */}
          {!isOpen && tooltip && (
            <span className="absolute top-0 right-0 w-3 h-3 bg-destructive rounded-full border-2 border-card animate-pulse"></span>
          )}
        </div>
      </div>
    </>
  );
};

export default SynapseWidget;